import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Space,
  Tag,
  Popconfirm,
  notification,
  Typography,
  Card,
  Badge,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  KeyOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { userService } from '../services/userService';

const { Title } = Typography;
const { Option } = Select;

const ROLES = ['Admin', 'Officer', 'Manager', 'Accounts'];

const roleColors = {
  Admin: 'red',
  Manager: 'purple',
  Officer: 'blue',
  Accounts: 'green',
};

const roleDescriptions = {
  Admin: 'Full access including User Management',
  Manager: 'Full access with deletion rights',
  Officer: 'All sections except Sales Invoices & PI',
  Accounts: 'Sales Invoices & PI section only',
};

export default function UserManagementPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Create modal
  const [createVisible, setCreateVisible] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createForm] = Form.useForm();

  // Edit modal
  const [editVisible, setEditVisible] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editForm] = Form.useForm();
  const [editingUser, setEditingUser] = useState(null);

  // Reset password modal
  const [pwdVisible, setPwdVisible] = useState(false);
  const [pwdLoading, setPwdLoading] = useState(false);
  const [pwdForm] = Form.useForm();
  const [pwdUserId, setPwdUserId] = useState(null);
  const [pwdUserName, setPwdUserName] = useState('');

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await userService.getUsers();
      setUsers(res.data || []);
    } catch (err) {
      notification.error({ message: 'Failed to load users', description: err.error || err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // ── Create ──────────────────────────────────────────────
  const handleCreate = async (values) => {
    setCreateLoading(true);
    try {
      await userService.createUser(values);
      notification.success({ message: 'User created successfully' });
      setCreateVisible(false);
      createForm.resetFields();
      fetchUsers();
    } catch (err) {
      notification.error({ message: 'Failed to create user', description: err.error || err.message });
    } finally {
      setCreateLoading(false);
    }
  };

  // ── Edit ────────────────────────────────────────────────
  const openEdit = (user) => {
    setEditingUser(user);
    editForm.setFieldsValue({
      fullName: user.fullName,
      username: user.username,
      email: user.email,
      role: user.role,
      isActive: user.isActive,
    });
    setEditVisible(true);
  };

  const handleEdit = async (values) => {
    setEditLoading(true);
    try {
      await userService.updateUser(editingUser.id, values);
      notification.success({ message: 'User updated successfully' });
      setEditVisible(false);
      fetchUsers();
    } catch (err) {
      notification.error({ message: 'Failed to update user', description: err.error || err.message });
    } finally {
      setEditLoading(false);
    }
  };

  // ── Delete ──────────────────────────────────────────────
  const handleDelete = async (id) => {
    try {
      await userService.deleteUser(id);
      notification.success({ message: 'User deleted successfully' });
      fetchUsers();
    } catch (err) {
      notification.error({ message: 'Failed to delete user', description: err.error || err.message });
    }
  };

  // ── Reset Password ──────────────────────────────────────
  const openResetPwd = (user) => {
    setPwdUserId(user.id);
    setPwdUserName(user.fullName);
    pwdForm.resetFields();
    setPwdVisible(true);
  };

  const handleResetPwd = async (values) => {
    setPwdLoading(true);
    try {
      await userService.resetPassword(pwdUserId, values.password);
      notification.success({ message: 'Password reset successfully' });
      setPwdVisible(false);
    } catch (err) {
      notification.error({ message: 'Failed to reset password', description: err.error || err.message });
    } finally {
      setPwdLoading(false);
    }
  };

  // ── Table columns ───────────────────────────────────────
  const columns = [
    {
      title: 'Full Name',
      dataIndex: 'fullName',
      key: 'fullName',
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
      render: (text) => <code>{text}</code>,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role) => (
        <Tag color={roleColors[role] || 'default'}>{role}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) =>
        isActive ? (
          <Badge status="success" text="Active" />
        ) : (
          <Badge status="error" text="Inactive" />
        ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEdit(record)}
          >
            Edit
          </Button>
          <Button
            size="small"
            icon={<KeyOutlined />}
            onClick={() => openResetPwd(record)}
          >
            Reset Password
          </Button>
          <Popconfirm
            title={`Delete user "${record.fullName}"?`}
            description="This action cannot be undone."
            onConfirm={() => handleDelete(record.id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
            disabled={record.id === currentUser?.id}
          >
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              disabled={record.id === currentUser?.id}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '0 0 24px 0' }}>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <UserOutlined style={{ fontSize: 24, color: '#1890ff' }} />
          <Title level={2} style={{ margin: 0 }}>User Management</Title>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setCreateVisible(true)}
        >
          Create User
        </Button>
      </div>

      {/* Role legend */}
      <Card size="small" style={{ marginBottom: 16, borderRadius: 8 }}>
        <Space wrap>
          {ROLES.map((role) => (
            <span key={role} style={{ marginRight: 16 }}>
              <Tag color={roleColors[role]}>{role}</Tag>
              <span style={{ fontSize: 12, color: '#666' }}>{roleDescriptions[role]}</span>
            </span>
          ))}
        </Space>
      </Card>

      <Card bordered={false} style={{ borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <Table
          dataSource={users}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20 }}
        />
      </Card>

      {/* ── Create Modal ─────────────────────────────────── */}
      <Modal
        title="Create New User"
        open={createVisible}
        onCancel={() => { setCreateVisible(false); createForm.resetFields(); }}
        footer={null}
        width={480}
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreate} style={{ marginTop: 16 }}>
          <Form.Item
            name="fullName"
            label="Full Name"
            rules={[{ required: true, message: 'Full name is required' }]}
          >
            <Input placeholder="e.g. Rahul Sharma" />
          </Form.Item>
          <Form.Item
            name="username"
            label="Username"
            rules={[
              { required: true, message: 'Username is required' },
              { pattern: /^[a-zA-Z0-9_]+$/, message: 'Only letters, numbers, underscore allowed' },
            ]}
          >
            <Input placeholder="e.g. rahul_sharma" />
          </Form.Item>
          <Form.Item name="email" label="Email">
            <Input type="email" placeholder="e.g. rahul@company.com" />
          </Form.Item>
          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: 'Password is required' },
              { min: 6, message: 'Minimum 6 characters' },
            ]}
          >
            <Input.Password placeholder="Minimum 6 characters" />
          </Form.Item>
          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: 'Role is required' }]}
          >
            <Select placeholder="Select role">
              {ROLES.map((r) => (
                <Option key={r} value={r}>
                  <Tag color={roleColors[r]}>{r}</Tag> — {roleDescriptions[r]}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => { setCreateVisible(false); createForm.resetFields(); }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={createLoading}>
                Create User
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Edit Modal ───────────────────────────────────── */}
      <Modal
        title={`Edit User — ${editingUser?.fullName}`}
        open={editVisible}
        onCancel={() => setEditVisible(false)}
        footer={null}
        width={480}
      >
        <Form form={editForm} layout="vertical" onFinish={handleEdit} style={{ marginTop: 16 }}>
          <Form.Item
            name="fullName"
            label="Full Name"
            rules={[{ required: true, message: 'Full name is required' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="username"
            label="Username"
            rules={[
              { required: true, message: 'Username is required' },
              { pattern: /^[a-zA-Z0-9_]+$/, message: 'Only letters, numbers, underscore allowed' },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="email" label="Email">
            <Input type="email" />
          </Form.Item>
          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: 'Role is required' }]}
          >
            <Select>
              {ROLES.map((r) => (
                <Option key={r} value={r}>
                  <Tag color={roleColors[r]}>{r}</Tag> — {roleDescriptions[r]}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="isActive" label="Status" valuePropName="checked">
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setEditVisible(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={editLoading}>
                Save Changes
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Reset Password Modal ─────────────────────────── */}
      <Modal
        title={`Reset Password — ${pwdUserName}`}
        open={pwdVisible}
        onCancel={() => setPwdVisible(false)}
        footer={null}
        width={400}
      >
        <Form form={pwdForm} layout="vertical" onFinish={handleResetPwd} style={{ marginTop: 16 }}>
          <Form.Item
            name="password"
            label="New Password"
            rules={[
              { required: true, message: 'Password is required' },
              { min: 6, message: 'Minimum 6 characters' },
            ]}
          >
            <Input.Password placeholder="Minimum 6 characters" />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="Confirm Password"
            dependencies={['password']}
            rules={[
              { required: true, message: 'Please confirm the password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="Re-enter password" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setPwdVisible(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={pwdLoading}>
                Reset Password
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
